/**
 * CategoryFilterSidebar — the facet column (STORY-003-005; rebuilt 2026-08).
 *
 * WHAT WAS WRONG. Five sections separated by nothing but a 24px gap, each
 * headed by grey uppercase text, one of which ("Рынок", "Цена, сом") was then
 * repeated verbatim by the control's own legend directly underneath it — so the
 * shipped page literally read "РЫНОК / Рынок / Все рынки" and "ЦЕНА, СОМ /
 * Цена, сом / [ ] — [ ]". The category list had no rules and no indent cue, so
 * a nine-item list of plain text was doing the work of a navigation tree.
 *
 * WHAT IT IS NOW. One ruled column. `.g-ruled` draws a real hairline between
 * facets — the "visible armature" raise — so the panel reads as a stack of
 * distinct decisions rather than as a paragraph of controls. Each facet is
 * headed ONCE: the duplicated legends inside `MarketFilterMultiselect`,
 * `PriceRangeInput` and `SortSelect` are now `sr-only` (still there for AT,
 * which needs the group to be named) while the visible heading is this file's.
 *
 * Section headings are `<h2>` so a screen reader can skip between filter groups
 * (story A11y note). The same component renders in the desktop static sidebar
 * AND inside the mobile filter Sheet — the page swaps the container, not this
 * content — so it must survive a 320px panel as well as a 256px column.
 *
 * Tokens via the `--g-*` mirror; strings from the `marketplace` namespace.
 */
import { type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CategoryTree } from "@/features/marketplace/CategoryTree";
import {
  MarketFilterMultiselect,
  type MarketOption,
} from "@/features/marketplace/MarketFilterMultiselect";
import {
  PriceRangeInput,
  type PriceRangeValue,
} from "@/features/marketplace/PriceRangeInput";
import { SortSelect, type SortValue } from "@/features/marketplace/SortSelect";
import { StockToggle } from "@/features/marketplace/StockToggle";

export interface CategoryFilterSidebarProps {
  /** Static market options (no public markets-list endpoint; story spec). */
  marketOptions: readonly MarketOption[];
  selectedMarketIds: readonly string[];
  onMarketChange: (ids: string[]) => void;

  inStockOnly: boolean;
  onInStockChange: (checked: boolean) => void;

  priceRange: PriceRangeValue;
  onPriceChange: (value: PriceRangeValue) => void;

  sort: SortValue;
  onSortChange: (value: SortValue) => void;

  /** Active category id (drives the tree highlight + the "All" reset enable). */
  activeCategoryId: string | null;
  /** Fires when a category node is picked (page resets pagination). */
  onCategorySelect: (categoryId: string) => void;
  /** Clears the category filter ("All categories" reset). */
  onCategoryReset: () => void;
  /**
   * Render the category facet at all (SCRUM-184). Defaults to `true` — browse,
   * where the tree IS the navigation model, keeps working without opting in.
   *
   * Search passes `false`: the tree is a STATIC list of every category, and the
   * search response carries no category data to scope it with, so on a results
   * page it listed all twelve regardless of the query — "noise rather than
   * helping filter". Hiding it is the honest reading of the owner's rule (show
   * options where they exist, otherwise remove); faking options from a static
   * list is the bug itself. A real facet has to aggregate over the WHOLE result
   * set rather than the returned page, or the sidebar would change under
   * pagination — that is the linked follow-up, not this prop.
   */
  showCategories?: boolean;
  /**
   * Accessible name for the `<aside>` landmark. The page renders the sidebar
   * twice (desktop column + mobile Sheet); each instance needs a DISTINCT label
   * so the two landmarks are individually distinguishable (axe landmark-unique).
   */
  ariaLabel?: string;
}

/** One ruled facet: a small-caps heading, an optional trailing control, a body. */
function Facet({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="flex flex-col gap-2.5 py-4 first:pt-0 last:pb-0">
      <div className="flex min-h-[1.25rem] items-center justify-between gap-2">
        <h2 className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function CategoryFilterSidebar({
  marketOptions,
  selectedMarketIds,
  onMarketChange,
  inStockOnly,
  onInStockChange,
  priceRange,
  onPriceChange,
  sort,
  onSortChange,
  activeCategoryId,
  onCategorySelect,
  onCategoryReset,
  showCategories = true,
  ariaLabel,
}: CategoryFilterSidebarProps): ReactElement {
  const { t } = useTranslation("marketplace");

  return (
    <aside
      aria-label={ariaLabel ?? t("aria.filters")}
      data-testid="category-filter-sidebar"
      className="g-ruled flex flex-col"
    >
      {showCategories ? (
      <Facet
        title={t("category.treeLabel")}
        action={
          // A text button, not an icon+label pill: it is a correction, and a
          // correction should be available without being advertised. Disabled
          // (not hidden) when there is nothing to clear, so the control does
          // not appear and disappear as the user browses.
          <button
            type="button"
            onClick={onCategoryReset}
            disabled={activeCategoryId === null}
            data-testid="category-reset"
            className="min-h-[44px] px-1 text-xs font-semibold text-[color:var(--g-primary)] underline-offset-4 hover:underline disabled:pointer-events-none disabled:text-[color:var(--g-ink-3)] disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {t("category.allCategories")}
          </button>
        }
      >
        <CategoryTree
          controlledActiveId={activeCategoryId}
          onSelect={onCategorySelect}
          aria-label={t("category.treeLabel")}
        />
      </Facet>
      ) : null}

      <Facet title={t("filters.market")}>
        <MarketFilterMultiselect
          options={marketOptions}
          selectedIds={selectedMarketIds}
          onChange={onMarketChange}
        />
      </Facet>

      <Facet title={t("filters.priceRange")}>
        <PriceRangeInput value={priceRange} onChange={onPriceChange} />
      </Facet>

      <Facet title={t("filters.availability")}>
        <StockToggle checked={inStockOnly} onChange={onInStockChange} />
      </Facet>

      <Facet title={t("filters.sort")}>
        <SortSelect value={sort} onChange={onSortChange} />
      </Facet>
    </aside>
  );
}
